import { HorizontalScrollView } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import {
  type ExtraWorkedTimeRequestEntity,
  type ExtraWorkedTimeRequestShift,
  type ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatus as ExtraWorkedTimeRequestStatusEnum,
  type ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { compareDesc, parseISO } from "date-fns";
import { useMemo } from "react";

import { useWorkerUnverifiedShifts } from "../../UnverifiedShifts/useWorkerUnverifiedShifts";
import { useGetExtraWorkedTimeRequests } from "../api/useGetExtraWorkedTimeRequests";
import { useDismissedExtraWorkedTimeRequests } from "../hooks/useDismissedExtraWorkedTimeRequests";
import { ExtraWorkedTimeRequestBanner } from "./ExtraWorkedTimeRequestBanner";
import { UnverifiedShiftBanner } from "./UnverifiedShiftBanner";

interface TransformedRequest {
  id: string;
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  shiftDate: Date;
  shiftId: string;
  updatedAt: string;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater;
  statusUpdateReason?: string | null;
}

export function ShiftHistoryBannerList() {
  const { data: extraWorkedTimeRequestsData } = useGetExtraWorkedTimeRequests();

  // Extract request IDs from API response for localStorage cleanup
  // Pass undefined when data hasn't loaded to avoid premature cleanup
  const requestIds = useMemo(
    () => extraWorkedTimeRequestsData?.data.map(({ id }) => id),
    [extraWorkedTimeRequestsData]
  );

  const { isRequestDismissed, dismissRequest } = useDismissedExtraWorkedTimeRequests(requestIds);

  const extraWorkedTimeRequests = useMemo((): TransformedRequest[] => {
    if (!extraWorkedTimeRequestsData?.data) {
      return [];
    }

    const shiftMap = new Map<string, ExtraWorkedTimeRequestShift>(
      extraWorkedTimeRequestsData.included
        .filter((item): item is ExtraWorkedTimeRequestShift => item.type === "shift")
        .map((shift) => [shift.id, shift])
    );

    // Sort by updatedAt descending (most recent first) and deduplicate by shiftId
    const deduplicatedRequests = [
      ...extraWorkedTimeRequestsData.data
        .toSorted((a, b) =>
          compareDesc(parseISO(a.attributes.updatedAt), parseISO(b.attributes.updatedAt))
        )
        .reduce((map, request) => {
          if (!map.has(request.attributes.shiftId)) {
            map.set(request.attributes.shiftId, request);
          }
          return map;
        }, new Map<string, ExtraWorkedTimeRequestEntity>())
        .values(),
    ];

    return deduplicatedRequests
      .filter(
        ({ attributes: { status }, id }) =>
          status !== ExtraWorkedTimeRequestStatusEnum.PENDING && !isRequestDismissed(id)
      )
      .map(
        ({
          attributes: {
            shiftId,
            status,
            isFullShiftPayment,
            updatedAt,
            statusUpdatedBy,
            statusUpdateReason,
          },
          id,
        }) => {
          const shift = shiftMap.get(shiftId);
          if (!shift) {
            return undefined;
          }

          const { facility, start, verified } = shift.attributes;

          return {
            id,
            status: status as TransformedRequest["status"],
            workplaceName: facility.name,
            shiftDate: parseISO(start),
            shiftId,
            updatedAt,
            isVerified: verified,
            isFullShiftPayment,
            statusUpdatedBy,
            statusUpdateReason,
          };
        }
      )
      .filter(isDefined);
  }, [extraWorkedTimeRequestsData, isRequestDismissed]);

  const { data: unverifiedShiftsData } = useWorkerUnverifiedShifts();

  const unverifiedShifts = useMemo(() => {
    if (!unverifiedShiftsData?.response) {
      return [];
    }

    return unverifiedShiftsData.response.map((shift) => ({
      shiftId: shift._id,
      workplaceName: shift.facility.name,
      shiftDate: parseISO(shift.start),
    }));
  }, [unverifiedShiftsData]);

  if (extraWorkedTimeRequests.length === 0 && unverifiedShifts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 6, marginX: -5 }}>
      <HorizontalScrollView stretch sx={{ px: 5 }}>
        {unverifiedShifts.map((shift) => (
          <UnverifiedShiftBanner
            key={shift.shiftId}
            workplaceName={shift.workplaceName}
            shiftDate={shift.shiftDate}
            shiftId={shift.shiftId}
          />
        ))}
        {extraWorkedTimeRequests.map((request) => (
          <ExtraWorkedTimeRequestBanner
            key={request.id}
            status={request.status}
            workplaceName={request.workplaceName}
            shiftDate={request.shiftDate}
            shiftId={request.shiftId}
            updatedAt={request.updatedAt}
            isVerified={request.isVerified}
            isFullShiftPayment={request.isFullShiftPayment}
            statusUpdatedBy={request.statusUpdatedBy}
            statusUpdateReason={request.statusUpdateReason}
            onDismiss={() => {
              dismissRequest(request.id);
            }}
          />
        ))}
      </HorizontalScrollView>
    </Box>
  );
}
